/**
 * The one chart the on-screen plan carries: the retirement corpus drawdown.
 *
 * The user platform draws it with Recharts. This project has no Recharts (and
 * adding a charting library for a single series would be a poor trade), so the
 * same plot is drawn as inline SVG: a faint horizontal grid, a monotone-free
 * polyline through the yearly readings, a marked point per reading, and the
 * axis labels thinned to the ends and a few steps between.
 *
 * The series is four or five readings, not a continuous curve, and the dots say
 * so — the same judgement the Recharts version makes.
 */

import React from "react";
import { rupees } from "../../../Helpers/portfolioPlanner/format";
import styles from "./portfolio-planner.module.css";

export const ChartFrame = ({
  title,
  height = 220,
  footer,
  children,
}: {
  title: string;
  height?: number;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className={styles.chartCard}>
    <span className={styles.chartTitle}>{title}</span>
    <div style={{ width: "100%", minWidth: 0, height }}>{children}</div>
    {footer}
  </div>
);

interface Point {
  label: string;
  value: number;
}

const VIEW_WIDTH = 720;
const VIEW_HEIGHT = 230;
const PAD = { top: 14, right: 16, bottom: 26, left: 92 };

export const CorpusArea = ({ data }: { data: Point[] }) => {
  if (!data || data.length < 2) {
    return <p className={styles.chartEmpty}>Not enough readings to plot.</p>;
  }

  const values = data.map((point) => point.value);
  const maxValue = Math.max(...values, 0);
  const minValue = Math.min(...values, 0);
  const span = maxValue - minValue || 1;

  const plotWidth = VIEW_WIDTH - PAD.left - PAD.right;
  const plotHeight = VIEW_HEIGHT - PAD.top - PAD.bottom;
  const x = (index: number) =>
    PAD.left + (index / (data.length - 1)) * plotWidth;
  const y = (value: number) =>
    PAD.top + plotHeight - ((value - minValue) / span) * plotHeight;

  const line = data
    .map((point, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(2)},${y(point.value).toFixed(2)}`)
    .join(" ");
  const area = `${line} L${x(data.length - 1).toFixed(2)},${(PAD.top + plotHeight).toFixed(
    2
  )} L${x(0).toFixed(2)},${(PAD.top + plotHeight).toFixed(2)} Z`;

  const gridSteps = 4;
  const gridLines = Array.from({ length: gridSteps + 1 }, (_, step) => {
    const value = minValue + (span * step) / gridSteps;
    return { value, y: y(value) };
  });

  // Thin the x labels so a 40-reading trajectory does not become a wall.
  const labelEvery = Math.max(1, Math.ceil(data.length / 7));

  return (
    <svg
      className={styles.chartSvg}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Projected corpus balance by year"
    >
      <defs>
        <linearGradient id="plannerCorpusFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.34} />
          <stop offset="60%" stopColor="#4f46e5" stopOpacity={0.1} />
          <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
        </linearGradient>
      </defs>

      {gridLines.map((grid) => (
        <g key={grid.value}>
          <line
            className={styles.chartGrid}
            x1={PAD.left}
            x2={VIEW_WIDTH - PAD.right}
            y1={grid.y}
            y2={grid.y}
          />
          <text
            className={styles.chartAxisText}
            x={PAD.left - 8}
            y={grid.y + 3}
            textAnchor="end"
          >
            {rupees(grid.value)}
          </text>
        </g>
      ))}

      <path d={area} fill="url(#plannerCorpusFill)" />
      <path className={styles.chartLine} d={line} />

      {data.map((point, index) =>
        index % labelEvery === 0 || index === data.length - 1 ? (
          <text
            key={`label-${point.label}`}
            className={styles.chartAxisText}
            x={x(index)}
            y={VIEW_HEIGHT - 8}
            textAnchor={
              index === 0 ? "start" : index === data.length - 1 ? "end" : "middle"
            }
          >
            {point.label}
          </text>
        ) : null
      )}

      {data.map((point, index) => (
        <circle
          key={`dot-${point.label}`}
          className={styles.chartDot}
          cx={x(index)}
          cy={y(point.value)}
          r={2.6}
        >
          <title>{`${point.label} · ₹${rupees(point.value)}`}</title>
        </circle>
      ))}
    </svg>
  );
};

export default CorpusArea;
